import React, {useEffect, useState} from "react";
import {useStores} from "../../../../stores/useStores";
import {useAsync} from "react-async";
import {useTranslation} from "react-i18next";
import {ToastMessageStatus} from "../../../../components/atoms/toastMessage/toastMessage";
import CreateEditNoticeTemplate from "../../../../components/templates/notices/createEditNoticeTemplate";
import {NoticeStatus} from "../../../../shared/enums/noticeStatus";
import {observer} from "mobx-react-lite";
import {RoutePaths} from "../../../../shared/routePaths";
import {useHistory} from "react-router";
import {runInAction} from "mobx";
import {Navigation} from "../../../../utils/navigation";
import {NoticePublishType} from "../../../../shared/enums/noticePublishType";
import {SaveNoticeDetails} from "../../../../shared/interfaces/saveNoticeDetails";
import {format} from "date-fns";
import {utcToZonedTime} from "date-fns-tz";
import UpdatesQuietHourNotificationModal from "../../../../components/organisms/updatesQuietHourNotificationModal/updatesQuietHourNotificationModal";

export interface ICreateNoticeProps {}

const CreateNotice: React.FC<ICreateNoticeProps> = (props) => {
  const {
    uiStore: {modalStore, toastMessageStore, noticesUiStore},
    uiStore,
    domainStore: {noticesDomainStore}
  } = useStores();

  const [
    isUpdatesQuietHourNotificationModalOpen,
    setIsUpdatesQuietHourNotificationModalOpen
  ] = useState(false);

  const timeOfVillage = utcToZonedTime(
    noticesDomainStore.editableNotice.selectedPublishType !==
      NoticePublishType.Scheduled
      ? new Date()
      : noticesDomainStore.editableNotice.selectedPublishDate.date!,
    noticesDomainStore.getTimeZone()
  );

  const shouldSeeQuietHoursNotificationConfirmation =
    noticesDomainStore.editableNotice.notificationEnabled.value &&
    (timeOfVillage.getHours() >= 20 || timeOfVillage.getHours() < 8) &&
    !noticesDomainStore.editableNotice.isMultiVillage;

  useEffect(() => {
    noticesDomainStore.getVillages();
  }, [noticesDomainStore]);

  runInAction(() => {
    noticesDomainStore.editableNotice.isMultiVillage =
      uiStore.isMultiVillageOptionSelected();
  });

  const saveNoticeInternal = (
    status: NoticeStatus
  ): Promise<SaveNoticeDetails> => {
    runInAction(() => {
      noticesDomainStore.editableNotice.status = status;
    });
    return noticesDomainStore.createNotice();
  };

  const history = useHistory();

  useEffect(() => {
    runInAction(() => {
      noticesUiStore.isCreatingOrEditing = true;
    });
  }, [noticesUiStore]);

  const getSuccessMessage = ({
    title,
    selectedPublishDate,
    selectedPublishType,
    status
  }: SaveNoticeDetails) => {
    title = title.trim() !== "" ? title : t("notice:titles.untitledNotice");
    if (
      selectedPublishType === NoticePublishType.Scheduled &&
      status !== NoticeStatus.Draft
    ) {
      return t("notice:messages.createScheduleSuccess", {
        title,
        dateTime: format(
          utcToZonedTime(
            selectedPublishDate!,
            noticesDomainStore.getTimeZone()
          ),
          "dd/MM/yyyy hh:mma"
        )
      });
    }
    return t(
      status === NoticeStatus.Draft
        ? "notice:messages.createDraftSuccess"
        : "notice:messages.createSuccess",
      {title}
    );
  };
  const saveNotice = useAsync({
    deferFn: (args) => saveNoticeInternal(args[0]),
    onReject: (error) => {
      toastMessageStore.displayToastMessage(
        t(
          noticesDomainStore.editableNotice.status === NoticeStatus.Draft
            ? "notice:messages.createDraftFailure"
            : "notice:messages.createFailure",
          {
            title:
              noticesDomainStore.editableNotice.title.text.trim() !== ""
                ? noticesDomainStore.editableNotice.title.text
                : t("notice:titles.untitledNotice")
          }
        ),
        ToastMessageStatus.Danger
      );
    },
    onResolve: (details: SaveNoticeDetails) => {
      noticesUiStore.currentNoticeListPageNumber = 1;
      toastMessageStore.displayToastMessage(
        getSuccessMessage(details),
        ToastMessageStatus.Success
      );
      noticesUiStore.resetPaginationSettings();
      Navigation.navigateWithSelectedVillageId(history, RoutePaths.Notices);
    }
  });

  const {t} = useTranslation(["translation", "notice"]);
  return (
    <>
      <UpdatesQuietHourNotificationModal
        toggle={() =>
          setIsUpdatesQuietHourNotificationModalOpen(
            !isUpdatesQuietHourNotificationModalOpen
          )
        }
        isOpen={isUpdatesQuietHourNotificationModalOpen}
        onPublishBtnClicked={() => saveNotice.run(NoticeStatus.Published)}
        isSaving={saveNotice.isPending}
      />

      <CreateEditNoticeTemplate
        isSaving={saveNotice.isPending}
        isLoading={false}
        notice={noticesDomainStore.editableNotice}
        publishAction={async () =>
          shouldSeeQuietHoursNotificationConfirmation
            ? setIsUpdatesQuietHourNotificationModalOpen(true)
            : noticesDomainStore.editableNotice.notificationEnabled.value
            ? modalStore.openModalWithContent({
                content: t(
                  noticesDomainStore.editableNotice.selectedPublishType ===
                    NoticePublishType.Scheduled
                    ? "notice:messages.sendNotificationScheduled"
                    : "notice:messages.sendNotification"
                ),
                title: t("notice:titles.sendNotification"),
                secondaryButtonText: t("translation:confirmations.negative"),
                canToggle: false,
                centered: true,
                onSecondaryClick: modalStore.toggle,
                customButtons: [
                  {
                    type: "primary",
                    onClick: () => {
                      saveNotice.run(NoticeStatus.Published);
                      modalStore.toggle();
                    },
                    text: t("translation:actions.publish")
                  }
                ]
              })
            : saveNotice.run(NoticeStatus.Published)
        }
        saveDraftAction={async () =>
          noticesDomainStore.editableNotice.selectedPublishDate.date === null
            ? saveNotice.run(NoticeStatus.Draft)
            : modalStore.openModalWithContent({
                content: t("notice:messages.saveDraft"),
                title: t("notice:titles.saveDraft"),
                secondaryButtonText: t("notice:actions.continueEditing"),
                canToggle: false,
                centered: true,
                onSecondaryClick: modalStore.toggle,
                customButtons: [
                  {
                    type: "primary-negative",
                    onClick: () => {
                      saveNotice.run(NoticeStatus.Draft);
                      modalStore.toggle();
                    },
                    text: t("notice:actions.saveDraft")
                  }
                ]
              })
        }
        hasUnsavedChanges={noticesDomainStore.editableNotice.hasChanges}
      />
    </>
  );
};

export default observer(CreateNotice);
